/**
 * Firebase wiring for the web player.
 *
 * The config values here are not secrets — every one of them ships inside any
 * client that talks to the project, and access is controlled by security rules
 * and callable functions, not by hiding these.
 */

import { initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  signInWithCustomToken,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  type Firestore,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: 'AIzaSyAHgy2t9_v7XEU_71uF5c_u1C_RQS8Kn6A',
  authDomain: 'company-tambola.firebaseapp.com',
  projectId: 'company-tambola',
  storageBucket: 'company-tambola.firebasestorage.app',
  messagingSenderId: '644296868179',
  appId: '1:644296868179:web:238bac86e27ebffca6a1a6',
};

const app = initializeApp(firebaseConfig);

/**
 * In-memory cache only.
 *
 * A game is ephemeral and entirely server-authoritative. Persisting it to
 * IndexedDB would let a stale board survive a refresh and show a player numbers
 * that no longer reflect the game.
 */
export const db: Firestore = initializeFirestore(app, {
  localCache: memoryLocalCache(),
});

export const auth: Auth = getAuth(app);

/** Functions live in Mumbai; the default region would fail to resolve them. */
export const functions: Functions = getFunctions(app, 'asia-south1');

/**
 * Point at the local emulator suite when served from loopback.
 *
 * Loopback rather than a build flag, so the same bundle works when a phone
 * reaches the host through `adb reverse` — the device sees 127.0.0.1 too.
 */
const isLoopback =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

if (isLoopback) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

export function callable<Req, Res>(name: string) {
  return httpsCallable<Req, Res>(functions, name);
}

export { getFirestore, signInWithCustomToken };
